import { 
  AlertCircle, 
  LayoutGrid, 
  Bot, 
  HardDrive, 
  GitBranch, 
  BarChart3,
  Clock,
  Users,
  Shield,
  Zap
} from "lucide-react";

export default function FeaturesGrid() {
  const features = [
    {
      icon: AlertCircle,
      title: "Incident Management",
      description: "Full ITIL-compliant lifecycle for efficient issue resolution. Smart prioritization, SLA tracking, and automated workflows.",
      highlights: ["Priority Matrix", "SLA Tracking", "KB Integration"],
      size: "large",
    },
    {
      icon: LayoutGrid,
      title: "Service Catalog",
      description: "Self-service portal with dynamic forms and automated fulfillment.",
      highlights: ["Role-Based Access", "SLA-Bound Items"],
      size: "standard",
    },
    {
      icon: Bot,
      title: "Amie AI Chatbot",
      description: "24/7 intelligent assistant with NLP-powered ticket creation, sentiment analysis, and smart routing.",
      highlights: ["Natural Language", "Auto-Classification", "Context Aware"],
      size: "standard",
    },
    {
      icon: HardDrive,
      title: "Asset Management",
      description: "Complete lifecycle tracking from procurement to retirement.",
      highlights: ["Barcode Scanning", "Warranty Tracking"],
      size: "standard",
    },
    {
      icon: GitBranch,
      title: "Change Management",
      description: "Streamlined CAB workflows with risk assessment and conflict detection.",
      highlights: ["CAB Approvals", "Blackout Windows"],
      size: "standard",
    },
    {
      icon: BarChart3,
      title: "Analytics & Reporting",
      description: "Deep insights with real-time dashboards, MTTR tracking, and SLA compliance monitoring.",
      highlights: ["Custom Dashboards", "Export to PDF/CSV", "Scheduled Reports"],
      size: "wide",
    },
  ];

  const getSizeClasses = (size) => {
    switch (size) {
      case "large":
        return "bento-large";
      case "tall":
        return "bento-tall";
      case "wide":
        return "bento-wide";
      case "highlight":
        return "";
      default:
        return "";
    }
  };

  return (
    <section id="features" className="section-padding" data-testid="features-section">
      <div className="container-main">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <span className="text-sm font-mono uppercase tracking-widest text-blue-600 mb-4 block">
            Features
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            Everything you need for modern IT service management
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Built from the ground up with ITIL v4 best practices, AI-powered automation, 
            and an intuitive user experience that requires zero training.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`feature-card ${getSizeClasses(feature.size)} ${feature.dark ? "feature-card-dark" : ""}`}
              data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div>
                <div className={`icon-container ${feature.dark ? "icon-container-dark" : ""}`}>
                  <feature.icon size={24} className={feature.dark ? "text-blue-400" : "text-slate-700"} />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className={`${feature.dark ? "text-slate-300" : "text-slate-600"} leading-relaxed mb-4`}>
                  {feature.description}
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-auto">
                {feature.highlights.map((highlight, i) => (
                  <span
                    key={i}
                    className={`text-xs font-medium px-3 py-1.5 ${
                      feature.dark 
                        ? "bg-white/10 text-white" 
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* USP Row */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 border-t border-slate-200">
          {[
            { icon: Clock, label: "Deploy in Weeks", sublabel: "Not months" },
            { icon: Users, label: "Minimal Training", sublabel: "Intuitive UX" },
            { icon: Zap, label: "40% Cost Reduction", sublabel: "Via automation" },
            { icon: Shield, label: "SOC 2 Type II", sublabel: "Enterprise security" },
          ].map((usp, index) => (
            <div key={index} className="text-center md:text-left">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 mb-4">
                <usp.icon size={24} className="text-blue-600" />
              </div>
              <div className="text-lg font-semibold text-slate-900">{usp.label}</div>
              <div className="text-sm text-slate-500">{usp.sublabel}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
